import { LoaderCircle } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | "children"
  | "onAnimationStart"
  | "onAnimationEnd"
  | "onAnimationIteration"
  | "onDrag"
  | "onDragStart"
  | "onDragEnd"
>;
import { indicatorTransition, pressScale } from "@/lib/motion";
import { cn } from "@/lib/utils";

const variants = {
  primary:
    "bg-accent text-accent-foreground can-hover:hover:bg-accent-strong active:bg-accent-strong",
  secondary:
    "border border-line bg-surface text-foreground can-hover:hover:bg-surface-muted active:bg-surface-muted",
  ghost: "bg-transparent text-foreground can-hover:hover:bg-surface-muted active:bg-surface-muted",
  danger:
    "border border-danger/30 bg-danger/8 text-danger can-hover:hover:bg-danger/14 active:bg-danger/14",
} as const;

const sizes = {
  sm: "h-9 px-3 text-sm",
  md: "h-11 px-4 text-sm",
  lg: "h-[50px] px-5 text-base",
} as const;

const base = [
  "inline-flex select-none items-center justify-center gap-2 rounded-control font-medium whitespace-nowrap",
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-outline)]",
];

/** A button's look on another element, such as a router link that navigates. */
export function buttonClass({ variant = "primary", size = "md", className }: { variant?: keyof typeof variants; size?: keyof typeof sizes; className?: string } = {}) {
  return cn(base, "no-underline", variants[variant], sizes[size], className);
}

export type ButtonProps = {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  loading?: boolean;
  /** Menu triggers change colour. Other buttons scale to 0.97. */
  press?: "scale" | "tint";
  ref?: Ref<HTMLButtonElement>;
  children?: ReactNode;
} & NativeButtonProps;

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  press = "scale",
  className,
  children,
  disabled,
  type = "button",
  onClick,
  ref,
  ...props
}: ButtonProps) {
  const reduce = useReducedMotion();
  const unavailable = Boolean(disabled);
  const quiet = unavailable || loading;

  return (
    <motion.button
      ref={ref}
      type={type}
      data-slot="button"
      data-variant={variant}
      data-press={press}
      aria-busy={loading || undefined}
      aria-disabled={quiet || undefined}
      disabled={unavailable}
      whileTap={press === "scale" && !quiet && !reduce ? { scale: pressScale } : undefined}
      transition={indicatorTransition(reduce)}
      className={cn(
        base,
        "disabled:border-transparent disabled:bg-surface-muted disabled:text-secondary disabled:opacity-100",
        variants[variant],
        sizes[size],
        press === "tint" && "active:bg-surface-muted",
        className,
      )}
      onClick={(event) => {
        if (quiet) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...props}
    >
      {loading ? (
        <LoaderCircle
          aria-hidden
          className={cn("size-4", reduce ? "opacity-70" : "animate-spin")}
          strokeWidth={1.75}
        />
      ) : null}
      {children}
    </motion.button>
  );
}
